<template>
  <a-modal
    class="devices"
    :title="$t('settings.mediaSettings')"
    @close="app.transition('HIDE_DEVICES_MODAL')"
  >
    <p class="text--small">{{ $t('settings.selectYourPreferredDevice') }}</p>
    <div class="form__row" v-if="app.isCameraRecommended">
      <div class="form__group">
        <label for="videoinput">{{ $t('settings.selectCamera') }}</label>
        <div 
          v-if="!app.state.matches('app.camera.notAvailable')"
          class="form--icon"
          :class="[isChanging.camera && '--loading']"
        >
          <a-icon v-if="isChanging.camera" name="sync" :width="16" :height="16" />
          <a-icon v-else name="camera" :width="16" :height="16" />
          <select
            class="form__control"
            name="videoinput"
            id="videoinput"
            v-model="camera"
            :disabled="isChanging.camera || isChanging.microphone"
            @change="handleDeviceChange('camera', $event.target.value, $event.target.value === 'default' ? $t('settings.defaultDevice') : cameras.find((camera) => camera.deviceId === $event.target.value).label)"
          >
            <option value="default">{{ $t('settings.defaultDevice') }}</option>
            <option v-for="(device, index) in cameras" :key="index" :value="device.deviceId">{{ device.label }}</option>
          </select>
        </div>
        <div v-else>
          <a-alert class="mb--0">
            <p class="mb--0">
              {{ $t('settings.cameraNotAvailable', { camera: cameraLabel}) }}
              <a-button
                variant="link-info"
                class="p--0 d--block mb--0 h--auto mt--2"
                @click="app.transition('SWITCH_MODAL', {
                  returnTo: 'SHOW_DEVICES_MODAL',
                  to: 'SHOW_CAMERA_PERMISSIONS_MODAL'
                })"
              >
              {{ $t('settings.howToEnableCamera') }}
              </a-button>
            </p>
          </a-alert>
        </div>
      </div>
      <div class="form__extra" v-if="!app.state.matches('app.camera.notAvailable')">
        <div class="devices__video">
          <video
            ref="video"
            id="devices-preview"
            class="media media--cover"
            :class="[(isChanging.camera || isChanging.microphone) && '--changing']"
            autoplay
            playsinline
            controls
            muted
          ></video>
          <div v-if="isChanging.camera || isChanging.microphone" class="devices__video__placeholder --loading">
          </div>
        </div>
      </div>
    </div>
    <div class="form__row" v-if="app.isMicrophoneRecommended">
      <div class="form__group">
        <label for="audioinput">{{ $t('settings.selectMicrophone') }}</label>
        <div 
          v-if="!app.state.matches('app.microphone.notAvailable')"
          class="form--icon" 
          :class="[isChanging.microphone && '--loading']"
        >
          <a-icon v-if="isChanging.microphone" name="sync" :width="16" :height="16" />
          <a-icon v-else name="microphone" :width="16" :height="16" />
          <select
            class="form__control"
            name="audioinput"
            id="audioinput"
            v-model="microphone"
            :disabled="isChanging.camera || isChanging.microphone"
            @change="handleDeviceChange('microphone', $event.target.value, $event.target.value === 'default' ? $t('settings.defaultDevice') : microphones.find((microphone) => microphone.deviceId === $event.target.value).label)"
            required
            >
            <option value="default">{{ $t('settings.defaultDevice') }}</option>
            <option v-for="(device, index) in microphones" :key="index" :value="device.deviceId">{{ device.label }}</option>
          </select>
        </div>
        <div v-else>
          <a-alert class="mb--0">
            <p class="mb--0">
              {{ $t('settings.microphoneNotAvailable', { microphone: microphoneLabel}) }}
              <a-button
                variant="link-info"
                class="p--0 d--block mb--0 h--auto mt--2"
                @click="app.transition('SWITCH_MODAL', {
                  returnTo: 'SHOW_DEVICES_MODAL',
                  to: 'SHOW_MICROPHONE_PERMISSIONS_MODAL'
                })"
              >
              {{ $t('settings.howToEnableMicrophone') }}
              </a-button>
            </p>
          </a-alert>
        </div>
      </div>
    </div>
    <div class="form__row">
      <div class="form__group">
        <label for="audiooutput">{{ $t('settings.selectSpeaker') }}</label>
        <div 
          class="form--icon"
          :class="[(isPlaying || isChanging.speaker) && '--loading']"
        >
          <a-icon v-if="isPlaying || isChanging.speaker" name="sync" :width="16" :height="16" />
          <a-icon v-else name="speaker" :width="16" :height="16" />
          <select
            class="form__control"
            name="audiooutput"
            id="audiooutput"
            v-model="speaker"
            @change="handleSpeakerChange($event.target.value, $event.target.value === 'default' ? $t('settings.defaultDevice') : speakers.find((speaker) => speaker.deviceId === $event.target.value).label)"
            :disabled="isPlaying || isChanging.speaker"
            required
          >
            <option value="default">{{ $t('settings.defaultDevice') }}</option>
            <option v-for="(device, index) in speakers" :key="index" :value="device.deviceId">{{ device.label }}</option>
          </select>
          
        </div>
      </div>
      <div class="form__extra">
        <a-button
          variant="outline-primary"
          block
          :disabled="isPlaying || isChanging.speaker"
          @click="play('speaker-test')"
        >
          {{ $t('settings.testSound') }}
        </a-button>
      </div>
    </div>
    <div class="form__row">
      <div class="form__group">
        <label for="audiooutput">{{ $t('settings.selectLanguage') }}</label>
        <div class="form--icon" :class="[isChangingLanguage && '--loading']">
          <a-icon v-if="isChangingLanguage" name="sync" :width="16" :height="16" />
          <a-icon v-else :name="`flag-${language}`.toLowerCase()" :width="16" :height="16" />
          <select 
            class="form__control"
            name="language" id="language"
            :value="language" 
            :disabled="isChangingLanguage"
            @change="changeLanguage($event.target.value)"
            required
          >
            <template v-if="languages">
              <option v-for="(code, index) in Object.keys(languages)" :value="code" :key="index">{{ languages[code].nativeName }}</option>
            </template>
          </select>
        </div>
      </div>
    </div>
    <div class="form__row" v-if="!app.state.matches('app.view.meeting.lobby') && !app.state.matches('app.view.meeting')">
      <div class="form__group">
        <label>{{ $t('alert.titleDataLimit') }}</label>
        <div class="form--icon data-limit ">
          <input
            class="form__control"
            v-model="user.dataLimit"
            v-mask="'#########'"
            @change="setDataLimit($event.target.value)"
          />
          <select
            class="form__control"
            name="language"
            id="language"
            :value="user.unitData"
            @change="changeUnitData($event.target.value)"
          >
            <template v-if="unitDataOptions">
              <option v-for="(code, index) in Object.keys(unitDataOptions)" :value="unitDataOptions[code].unit" :key="index">{{ unitDataOptions[code].unit }}</option>
            </template>
          </select>
        </div>
      </div>
    </div>
    <a-button
      v-if="showConnectionsButton"
      variant="outline-primary"
      @click="app.transition('SWITCH_MODAL', {
        returnTo: 'SHOW_DEVICES_MODAL',
        to: 'SHOW_CONNECTIONS_MODAL'
      })"
    >
      <a-icon name="eye"  />
    </a-button>
    <template #footer>
      <a-button
        v-if="app.isMicrophoneRecommended && app.isCameraRecommended && !app.state.matches('app.camera.notAvailable') && !app.state.matches('app.microphone.notAvailable')"
        :disabled="cameras.length === 0 || microphones.length === 0"
        variant="outline-primary"
        @click="restartMedia({ restartVideo: true, restartAudio: true })"
      >
        {{ $t('settings.restart') }}
      </a-button>
      <a-button
        @click="app.transition('HIDE_DEVICES_MODAL')"
      >
        {{ $t('settings.done') }}
      </a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, reactive, watchEffect } from 'vue'
import { useAppStore, useUserStore } from '@/stores'
import { 
  useLocalStream,
  useLanguage,
  useLanguages,
  useDevicesList,
  useSpeaker
} from '@/composables'
import {getLocalStorage, setLocalStorage} from '@browser'
import AModal from '@/components/AModal.vue'

const {
  videoInputs: cameras,
  audioInputs: microphones,
  audioOutputs: speakers,
} = useDevicesList()

const { 
  change: changeSpeaker,
  play,
  isPlaying
} = useSpeaker()

const storage = getLocalStorage('devices')
const app = useAppStore()
const user = useUserStore()
const { stream, video: localVideoStream, videoUpdatedAt, changeDevice, replaceDevice, restartMedia } = useLocalStream()
const languages = useLanguages()
const { isChangingLanguage, language, change: changeLanguage } = useLanguage()
const unitDataOptions = [{unit: 'KB'},{unit: 'MB'},{unit: 'GB'}]
const video = ref()
const microphone = ref(storage?.microphone?.deviceId || 'default')
const camera = ref(storage?.camera?.deviceId || 'default')
const speaker = ref(storage?.speaker?.deviceId || 'default')
const showConnectionsButton = !!window.localStorage.getItem('airelink:logger')

const isChanging = reactive({
  camera: false,
  microphone: false,
  speaker: false
})
const microphoneLabel = ref(storage?.microphone?.label || '')
const cameraLabel = ref(storage?.camera?.label || '')

watchEffect(() => {
  // Also depend on videoUpdatedAt: switching devices adds the new track to the same
  // MediaStream object, so the stream ref alone does not change
  videoUpdatedAt.value
  if (video?.value) {
    if (video.value.srcObject !== localVideoStream.value) {
      video.value.srcObject = localVideoStream.value
    }
    // In Firefox a preview attached while the stream was still empty stays paused
    // after the track arrives — autoplay alone does not resume it
    video.value.play().catch(() => {})
  }
})

// Last successfully applied selection per device, used to roll back a failed switch
const lastApplied = {
  camera: { value: camera.value, label: cameraLabel.value },
  microphone: { value: microphone.value, label: microphoneLabel.value },
}

const handleDeviceChange = async (type, deviceId, deviceLabel) => {
  isChanging[type] = true
  const actualDeviceId = deviceId === 'default' ? '' : deviceId
  const muted = {
    video: user.hasCameraMuted,
    audio: user.hasMicrophoneMuted
  }
  try {
    if (app.state.matches('app.view.meeting')) {
      await replaceDevice(type, actualDeviceId, deviceLabel, { muted })
    } else {
      await changeDevice(type, actualDeviceId, deviceLabel, { muted })
    }
    lastApplied[type] = { value: deviceId, label: deviceLabel }
  } catch (error) {
    console.error(`[handleDeviceChange] Switching ${type} failed:`, error)
    // Roll back the selection and the stored constraint to the last working device,
    // otherwise the UI and localStorage point at a device that was never acquired
    const previous = lastApplied[type]
    if (type === 'camera') camera.value = previous.value
    if (type === 'microphone') microphone.value = previous.value
    stream.constraints.update(type, previous.value === 'default' ? '' : previous.value, previous.label)
  } finally {
    isChanging[type] = false
  }
}

const handleSpeakerChange = async (deviceId, deviceLabel) => {
  isChanging.speaker = true
  const actualDeviceId = deviceId === 'default' ? '' : deviceId
  try {
    await changeSpeaker(actualDeviceId, deviceLabel)
  } finally {
    isChanging.speaker = false
  }
}

const changeUnitData = (unit) => {
  user.setUnitData(unit)
  storage.unitData = user.unitData
  setLocalStorage('devices', storage)
}

const setDataLimit = (val) => {
  // value empty or not a number set to 0
  const value = (!val || isNaN(val)) ? 0 : parseInt(val, 10)
  user.setDataLimit(value)
  storage.dataLimit = user.dataLimit
  setLocalStorage('devices', storage)
}
</script>

<style lang="scss">
$animation-duration:          1s;
$devices-media-height:        (divide(2, 3) * 100px);
$devices-placeholder-bg:      rgba($primary, 0.25);

.devices {
  .--loading {
    .icon {
      animation: rotate $animation-duration linear infinite;
    }
  }
  .--playing {
    .icon {
      animation: pulse $animation-duration linear infinite;
    }
  }
  &__video {
    position: relative;
    &__placeholder {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: $devices-placeholder-bg;
      animation: light-pulse $animation-duration linear infinite;
    }

  }
  .media {
    height: $devices-media-height;
    border-radius: $border-radius;
    &.--changing {
      opacity: 0;
    }
    
  }
  @media (max-width: $breakpoint-sm) {
    .form__row .form__group {
      margin-right: 0;
    }
    .form__extra {
      display: none;
    }
  }

  .data-limit{
    display: flex;
    gap: 10px;
  }
}
</style>
